/**
 * Single source of truth for which routes require an authenticated session.
 *
 * Both the server-side proxy (`src/proxy.ts`) and the client nav
 * (`useNavAuthSync` in `src/components/layout/useNavAuth.ts`, plus the shell's
 * choice of actions fallback) call this so the protected surface can't drift
 * between them — adding a guarded route means editing one list.
 *
 * `/host/onboarding` is intentionally included (it lives under `/host/`): it
 * still requires a session. The host-vs-non-host distinction is a separate
 * concern enforced server-side by the `HostGate` in the `(portal)` layout.
 *
 * `/events` is included and `/search` is NOT, even though both are
 * `driverFeatures`-gated. The difference is that `/events` reads a
 * bearer-guarded feed, so an anonymous visitor can only ever reach a page that
 * errors. That distinction has to be drawn HERE rather than left to the page's
 * own `redirect(...)`, because of the order the proxy runs in: the auth check
 * comes first, and an unauthenticated request that survives it falls through to
 * the driver-flag gate, where no access token means the catalog fallback
 * (`false`) and a genuine 404. So a route that is both auth-required and
 * driver-gated but missing from this list answers a signed-out visitor — or an
 * expired session, or a shared link — with a bare 404, and its own `/auth`
 * redirect never runs.
 *
 * `/messages` is included and is NOT driver-gated. Messaging is account-scoped:
 * a host with `driverFeatures` off still has an inbox, so the flag must not gate
 * it. Without the entry here, a chat link from an email opened in a signed-out
 * browser answers with a 404 rather than a trip through `/auth`.
 *
 * Keep this module dependency-free so it is safe to import from the edge proxy.
 */
export function isProtectedPath(pathname: string): boolean {
  return (
    pathname.startsWith("/driver") ||
    pathname === "/host" ||
    pathname.startsWith("/host/") ||
    pathname.startsWith("/account") ||
    pathname.startsWith("/messages") ||
    pathname.startsWith("/reserve") ||
    pathname.startsWith("/support/new") ||
    pathname === "/events"
  );
}
